import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChicagoStars } from "../components/cod/Logo";
import { PublicHeader } from "../components/cod/PublicHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { marketApi } from "../lib/api";
import { Search, ArrowRight, ArrowLeft, Clock, DollarSign, Award, Sparkles, Star, TrendingUp, MessageSquare, Info } from "lucide-react";
import { useAuth } from "../lib/auth";
import { IMG } from "../lib/images";
import { Seo } from "../components/cod/Seo";

const LABEL_META = {
  featured: { icon: Star,       color: "var(--cod-red)",  desc: "Highlighted by COD because it leads to high-demand, well-paying placements." },
  popular:  { icon: TrendingUp, color: "var(--cod-navy)", desc: "Frequently requested by Chicago Opportunity Desk participants." },
  new:      { icon: Sparkles,   color: "#0a7c44",         desc: "Recently added to the COD service catalog." },
};

function LabelBadge({ label }) {
  const m = LABEL_META[label];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border bg-white cursor-help"
            style={{ color: m.color, borderColor: m.color }}
            data-testid={`label-${label}`}
          >
            <Icon size={10}/> {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">{m.desc}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function OpportunityCard({ o, onClick, badge }) {
  const isAvailable = o.service_group === "Employment Services";
  return (
    <Card className={`cod-card-elev overflow-hidden border-0 shadow-sm h-full flex flex-col ${isAvailable ? "cursor-pointer" : "cursor-pointer opacity-90"}`}
          onClick={onClick} data-testid={`opp-${o.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
          <Badge variant="secondary" className="capitalize">{o.category}</Badge>
          <div className="flex flex-wrap gap-1">
            {isAvailable ? (
              <Badge className="text-[10px] uppercase tracking-wider" style={{background:"var(--cod-red)", color:"#fff"}}
                     data-testid={`opp-${o.id}-available`}>Available Now</Badge>
            ) : (
              <Badge className="text-[10px] uppercase tracking-wider" style={{background:"#FEF3C7", color:"#92400E"}}
                     data-testid={`opp-${o.id}-coming-soon`}>Coming Soon</Badge>
            )}
            {(o.labels || []).map(l => <LabelBadge key={l} label={l}/>)}
            {badge}
          </div>
        </div>
        {o.service_group && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{o.service_group}</span>}
        <CardTitle className="font-serif text-xl leading-snug mt-1">{o.name}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <p className="text-sm text-slate-700 leading-relaxed line-clamp-3">{o.description}</p>
        <div className="flex flex-wrap gap-3 mt-4 text-xs text-slate-600">
          {o.duration && <span className="flex items-center gap-1"><Clock size={12}/> {o.duration}</span>}
          {o.cost && <span className="flex items-center gap-1"><DollarSign size={12}/> {o.cost}</span>}
        </div>
        {o.credential && <div className="text-xs text-slate-600 mt-1 flex items-center gap-1"><Award size={12}/> {o.credential}</div>}
        <div className="mt-auto pt-4 text-sm font-medium flex items-center gap-1" style={{ color: "var(--cod-navy)" }}>
          {isAvailable ? "Learn more" : "View details · Not yet available"} <ArrowRight size={14}/>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Marketplace() {
  const [opps, setOpps] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [facets, setFacets] = useState({ categories: [], service_groups: [], cost_tiers: [], labels: [] });
  const [recs, setRecs] = useState({ items: [], plan_id: null, reason: "" });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [costTier, setCostTier] = useState("");
  const [label, setLabel] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    marketApi.opportunities({
      q: q || undefined, category: cat || undefined,
      cost_tier: costTier || undefined, label: label || undefined,
    }).then(setOpps).catch(()=>{});
  }, [q, cat, costTier, label]);

  useEffect(() => {
    marketApi.bundles().then(setBundles).catch(()=>{});
    marketApi.facets().then(setFacets).catch(()=>{});
    if (user?.role === "participant") {
      marketApi.recommendations().then(setRecs).catch(()=>{});
    }
  }, [user]);

  const showingFiltered = q || cat || costTier || label;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Seo
        title="Marketplace"
        description="Explore the full COD service catalog — CPR/BLS, Food Manager, Security Training, CNA, PCT, Phlebotomy, EKG, Medical Coding, Home Care, Government Contract Accelerator, and more."
        path="/marketplace"
        schema="ProfessionalService"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Marketplace", path: "/marketplace" }]}
      />
      <PublicHeader/>
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900">Opportunities Catalog</h1>
            <p className="mt-4 text-lg text-slate-700 max-w-2xl">Credentials, training, and supports you can pursue through COD. Each pathway includes scholarships, navigation, and direct connections to employers and partners.</p>
            {!user && <p className="mt-3 text-sm text-slate-500" data-testid="anon-hint">Sign in or start a conversation with the Navigator to see opportunities personalized to your plan.</p>}
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="rounded-lg overflow-hidden border-4 border-[var(--cod-navy)] shadow-md">
              <img src={IMG.training} alt="Workforce training" loading="lazy" decoding="async" className="w-full h-56 object-cover"/>
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
        {/* Personalized recommendations (only when participant + active plan) */}
        {recs.items.length > 0 && (
          <section className="mb-10" data-testid="recommendations-section">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles size={20} style={{color:"var(--cod-red)"}}/>
              <p className="cod-eyebrow">Recommended for you</p>
            </div>
            <h2 className="font-serif text-2xl text-slate-900">From your active Opportunity Plan</h2>
            <p className="text-sm text-slate-600 mt-1">{recs.reason || "Tied to your active plan."}</p>
            <div className="cod-rule-red mt-3 mb-5"/>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recs.items.map(o => (
                <OpportunityCard
                  key={o.id} o={o}
                  onClick={()=>navigate(`/marketplace/${o.id}`)}
                  badge={<Badge style={{background:"var(--cod-red)"}} className="text-white text-[10px] uppercase tracking-wider">In your plan</Badge>}
                />
              ))}
            </div>
          </section>
        )}

        {/* Filter row */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="filter-row">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={16} className="absolute left-3 top-3 text-slate-400"/>
            <Input placeholder="Search in your own words..." value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="market-search"/>
          </div>
          <select value={cat} onChange={e=>setCat(e.target.value)} className="border rounded-md px-3 bg-white" data-testid="market-category">
            <option value="">All categories</option>
            {facets.categories.map(c => <option key={c} value={c}>{c.replace("_"," ")}</option>)}
          </select>
          <select value={costTier} onChange={e=>setCostTier(e.target.value)} className="border rounded-md px-3 bg-white" data-testid="market-cost">
            <option value="">Any cost</option>
            {facets.cost_tiers.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select value={label} onChange={e=>setLabel(e.target.value)} className="border rounded-md px-3 bg-white" data-testid="market-label">
            <option value="">All</option>
            {facets.labels.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
          {showingFiltered && (
            <Button variant="outline" onClick={()=>{ setQ(""); setCat(""); setCostTier(""); setLabel(""); }} data-testid="clear-filters">Clear</Button>
          )}
        </div>

        {bundles.length > 0 && !showingFiltered && (
          <div className="mb-10" data-testid="bundles-section">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-serif text-2xl text-slate-900">Curated Bundles</h2>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 cursor-help"><Info size={12}/> What&apos;s a bundle?</span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">A bundle is a curated group of opportunities that together support a complete goal — like full healthcare entry or food-service hiring.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="cod-section-rule mt-3 mb-5"/>
            <div className="grid md:grid-cols-2 gap-4">
              {bundles.map(b => (
                <Card key={b.id} className="border-l-4" style={{ borderLeftColor: "var(--cod-red)" }} data-testid={`bundle-${b.id}`}>
                  <CardHeader><CardTitle className="font-serif text-xl">{b.name}</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{b.description}</p>
                    <div className="text-xs text-slate-500 mt-2">{b.opportunities?.length || 0} opportunities</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="cod-eyebrow">All Pathways</p>
            <h2 className="font-serif text-3xl text-slate-900 mt-2" data-testid="opps-count">{opps.length} opportunities available</h2>
            <div className="cod-section-rule mt-4"/>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="opps-grid">
          {opps.map(o => (
            <OpportunityCard key={o.id} o={o} onClick={()=>navigate(`/marketplace/${o.id}`)}/>
          ))}
        </div>
        {opps.length === 0 && (
          <div className="text-center py-12 text-slate-500" data-testid="no-results">
            <p>No opportunities match those filters.</p>
            <p className="text-sm mt-2">Try clearing filters, or ask the Navigator to help you find what you need.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export function MarketplaceDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { marketApi.opportunity(id).then(setOpp).catch(()=>setOpp(null)); }, [id]);

  if (!opp) return <div className="min-h-screen flex items-center justify-center bg-white">Loading…</div>;

  const startNavigator = () => {
    const slug = opp.slug || "";
    if (user) {
      navigate(`/app/navigator?opportunity=${encodeURIComponent(slug)}`);
    } else {
      navigate(`/start?opportunity=${encodeURIComponent(slug)}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Seo
        title={opp.name}
        description={(opp.description || "").slice(0, 180)}
        path={`/marketplace/${opp.slug || id}`}
        schema="ProfessionalService"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Marketplace", path: "/marketplace" },
          { name: opp.name, path: `/marketplace/${opp.slug || id}` },
        ]}
      />
      <PublicHeader/>
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button onClick={()=>navigate(-1)} className="text-sm text-slate-600 hover:text-[var(--cod-navy)] flex items-center gap-1 mb-6" data-testid="opp-back"><ArrowLeft size={14}/> Back</button>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Badge className="capitalize bg-[var(--cod-navy-soft)] text-[var(--cod-navy)] hover:bg-[var(--cod-navy-soft)]">{opp.category}</Badge>
            {opp.service_group && <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{opp.service_group}</span>}
            {(opp.labels || []).map(l => <LabelBadge key={l} label={l}/>)}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900" data-testid="opp-name">{opp.name}</h1>
          <div className="cod-rule-red mt-5"/>
        </div>
      </section>
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <p className="text-lg text-slate-700 leading-relaxed">{opp.description}</p>

          <div className="grid sm:grid-cols-3 gap-4">
            <Card><CardContent className="pt-6"><Clock size={20} className="mb-2" style={{color:"var(--cod-navy)"}}/><div className="text-xs uppercase tracking-wide text-slate-500">Duration</div><div className="text-slate-900 font-medium">{opp.duration||"—"}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><DollarSign size={20} className="mb-2" style={{color:"var(--cod-navy)"}}/><div className="text-xs uppercase tracking-wide text-slate-500">Cost</div><div className="text-slate-900 font-medium">{opp.cost||"—"}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><Award size={20} className="mb-2" style={{color:"var(--cod-navy)"}}/><div className="text-xs uppercase tracking-wide text-slate-500">Credential</div><div className="text-slate-900 font-medium">{opp.credential||"—"}</div></CardContent></Card>
          </div>

          {opp.outcomes && (
            <div><h3 className="font-serif text-xl mb-2">What to expect</h3>
              <p className="text-slate-700">{opp.outcomes}</p></div>)}
          {opp.eligibility && (
            <div><h3 className="font-serif text-xl mb-2">Eligibility</h3>
              <p className="text-slate-700">{opp.eligibility}</p></div>)}

          {/* Prerequisites */}
          {opp.prerequisite_opportunities?.length > 0 && (
            <section data-testid="prereqs">
              <h3 className="font-serif text-xl mb-3">Prerequisites</h3>
              <p className="text-sm text-slate-600 mb-3">Complete or be enrolled in these first.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {opp.prerequisite_opportunities.map(p => (
                  <Link key={p.id} to={`/marketplace/${p.id}`} className="block border rounded-md p-3 hover:bg-slate-50" data-testid={`prereq-${p.id}`}>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.service_group} · {p.duration}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Builds toward */}
          {opp.builds_toward_opportunities?.length > 0 && (
            <section data-testid="builds-toward">
              <h3 className="font-serif text-xl mb-3">Builds toward</h3>
              <p className="text-sm text-slate-600 mb-3">Completing this opens up these next pathways.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {opp.builds_toward_opportunities.map(p => (
                  <Link key={p.id} to={`/marketplace/${p.id}`} className="block border rounded-md p-3 hover:bg-slate-50" data-testid={`build-${p.id}`}>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.service_group} · {p.duration}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar: CTAs + Related CSF supports */}
        <aside className="space-y-6">
          {opp.service_group === "Employment Services" ? (
            <Card className="border-2" style={{borderColor:"var(--cod-red)"}}>
              <CardContent className="pt-6">
                <Badge className="mb-2 text-[10px] uppercase tracking-wider"
                       style={{background:"var(--cod-red)", color:"#fff"}}
                       data-testid="opp-available-now">Available Now</Badge>
                <p className="cod-eyebrow mb-2">Take the next step</p>
                <p className="text-sm text-slate-600 mb-4">Start a real conversation with the Opportunity Navigator — pre-oriented on this opportunity, so you don&apos;t have to re-explain.</p>
                <Button size="lg" onClick={startNavigator} className="w-full" style={{background:"var(--cod-red)"}} data-testid="opp-pursue">
                  <MessageSquare size={16} className="mr-2 flex-shrink-0"/>
                  <span className="truncate">Start Navigator about this</span>
                  <ArrowRight size={16} className="ml-2 flex-shrink-0"/>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2" style={{borderColor:"#F59E0B"}}>
              <CardContent className="pt-6">
                <Badge className="mb-2 text-[10px] uppercase tracking-wider"
                       style={{background:"#FEF3C7", color:"#92400E"}}
                       data-testid="opp-coming-soon">Coming Soon</Badge>
                <p className="cod-eyebrow mb-2">Not yet available</p>
                <p className="text-sm text-slate-600 mb-4">Chicago Opportunity Desk is launching in phases. This service will become active as our network and capacity expand. In the meantime, our <strong>Employment &amp; Staffing</strong> services are available now.</p>
                <Button size="lg" onClick={()=>navigate("/start?intent=find_work")} className="w-full"
                        style={{background:"var(--cod-red)"}} data-testid="opp-alt-cta">
                  Find Employment Instead <ArrowRight size={16} className="ml-2 flex-shrink-0"/>
                </Button>
              </CardContent>
            </Card>
          )}

          {opp.related_csf_services?.length > 0 && (
            <Card data-testid="csf-services">
              <CardHeader className="pb-2">
                <p className="cod-eyebrow">Related supports</p>
                <CardTitle className="font-serif text-lg">Helpful while you pursue this</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {opp.related_csf_services.map(s => (
                  <Link key={s.id} to={`/marketplace/${s.id}`} className="block hover:bg-slate-50 -mx-2 px-2 py-2 rounded" data-testid={`csf-${s.id}`}>
                    <p className="font-medium text-slate-900 text-sm">{s.name}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{s.description}</p>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}
        </aside>
      </main>
    </div>
  );
}
